import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import i18n from '@/js/i18n.js'
import { f7 } from 'framework7-vue'
import $dayjs from '@/js/day.js'
import _ from 'lodash'
import { useApimoneyStore } from '../pinia-stores/apimoney.js'
import { useAuthStore } from '../pinia-stores/auth.js'
import { useAccountStore } from '../pinia-stores/account.js'
import { useTransactionStatus } from './useTransactionStatus.js'
import { useDesktopScreen, useTabletScreen } from './useMediaQuery.js'

export function useCharts(options = {}) {
  const {
    orderBy: orderByOption,
    isDesc: isDescOption,
    orderingDir: orderingDirOption,
    groupBy: groupByOption,
    dataValue: dataValueOption,
    axisData: axisDataOption,
    area_name: area_nameOption,
    barData: barDataOption,
    barLabel: barLabelOption,
    bar_name: bar_nameOption,
    pieData: pieDataOption,
    pieLabel: pieLabelOption,
    areaChartSeries: areaChartSeriesOption,
    barChartSeries: barChartSeriesOption,
    pieChartSeries: pieChartSeriesOption,
    areaChartOptions: areaChartOptionsOption,
    barChartOptions: barChartOptionsOption,
    pieChartOptions: pieChartOptionsOption,
    groupingCriteria,
    updateChart: updateChartFn,
    updateTheme: updateThemeFn,
    setTransactionList: setTransactionListFn,
    users: usersOption,
  } = options

  const { transactionStatus } = useTransactionStatus()

  const apimoneyStore = useApimoneyStore()
  const authStore = useAuthStore()
  const accountStore = useAccountStore()

  const bankAccount = computed(() => apimoneyStore.bankAccount)
  const bankAccountList = computed(() => apimoneyStore.bankAccountList)
  const standard = computed(() => apimoneyStore.standard)
  const business = computed(() => apimoneyStore.business)
  const accountList = computed(() => apimoneyStore.accountList)
  const transactionList = computed(() => apimoneyStore.transactionList)
  const apiWallet = computed(() => apimoneyStore.apiWallet)
  const structuredTransactionList = computed(() => apimoneyStore.structuredTransactionList)

  const profile = computed(() => authStore.profile)
  const isDeskTop = useDesktopScreen()
  const isTablet = useTabletScreen().matches
  const documentsUrl = computed(() => authStore.documentsUrl)
  const document = computed(() => authStore.document)
  const locale = computed(() => authStore.locale)
  const getProfile = computed(() => authStore.getProfile)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isProfileLoaded = computed(() => authStore.isProfileLoaded)
  const getusererror = computed(() => authStore.getusererror)

  const userAccount = computed(() => accountStore.userAccount)

  function getInstanceState(key, option, fallback) {
    if (option?.value !== undefined) return option.value
    const self = getCurrentInstance()?.proxy
    return self?.[key] ?? fallback
  }

  function setInstanceState(key, option, value) {
    if (option?.value !== undefined) {
      option.value = value
    } else {
      const self = getCurrentInstance()?.proxy
      if (self) self[key] = value
    }
  }

  function selectSortingCriteria(value) {
    f7.popover.close('.sortingPopup')
    const currentOrderBy = getInstanceState('orderBy', orderByOption, null)
    const currentIsDesc = getInstanceState('isDesc', isDescOption, false)
    const newIsDesc = !currentIsDesc

    setInstanceState('orderBy', orderByOption, value)
    setInstanceState('isDesc', isDescOption, newIsDesc)
    setInstanceState('orderingDir', orderingDirOption, newIsDesc ? 'desc' : 'asc')

    const _groupBy = getInstanceState('groupBy', groupByOption, 'transactionYearMonth')
    doSetTransactionList(_groupBy, false)
  }

  function getCurrentUser(userId) {
    const _users = usersOption?.value ?? getCurrentInstance()?.proxy?.users ?? []
    return _.find(_users, ['userId', userId])
  }

  function currentTransactionList(transactionListArg, value) {
    const _orderBy = getInstanceState('orderBy', orderByOption, 'creation_date')
    const _orderingDir = getInstanceState('orderingDir', orderingDirOption, 'desc')

    return _.chain(transactionListArg)
      .orderBy([_orderBy], [_orderingDir])
      .map((transaction) => ({
        ...transaction,
        transactionYearQuarter: `${$dayjs(transaction.creation_date).quarter()}-${$dayjs(transaction.creation_date).year()}`,
        transactionYearMonth: $dayjs(transaction.creation_date).format('MM-YYYY'),
        transactionYearMonthWeek: `${$dayjs(transaction.creation_date).week()}-${$dayjs(transaction.creation_date).year()}`,
        transactionYearMonthDay: $dayjs(transaction.creation_date).format('DD-MM-YYYY'),
      }))
      .groupBy(value)
      .map((objs, key) => ({
        groupBy: key,
        currency: objs[0].currency,
        subTotalReceived: _.sumBy(objs, (item) =>
          parseFloat(item.amount) > 0 ? item.amount : 0
        ),
        subTotalPaid: _.sumBy(objs, (item) =>
          parseFloat(item.amount) < 0 ? item.amount : 0
        ),
        subTotal: _.sumBy(objs, 'amount'),
        items: objs,
      }))
      .value()
  }

  function doSetTransactionList(value, init_status) {
    setInstanceState('groupBy', groupByOption, value)
    f7.popover.close('.sortingPopup')
    f7.preloader.show('multi')

    const _transactionList = transactionList.value
    const _groupingCriteria = groupingCriteria?.value ?? getCurrentInstance()?.proxy?.groupingCriteria ?? []

    const aera_transaction_list = [
      'transactionYearQuarter',
      'transactionYearMonth',
      'transactionYearMonthWeek',
      'transactionYearMonthDay',
    ].includes(value)
      ? currentTransactionList(_transactionList, value)
      : currentTransactionList(_transactionList, 'transactionYearMonth')

    setInstanceState('dataValue', dataValueOption, aera_transaction_list.map((item) => item.subTotal))
    setInstanceState('axisData', axisDataOption, aera_transaction_list.map((item) => item.groupBy))
    setInstanceState(
      'area_name',
      area_nameOption,
      ['transactionYearQuarter', 'transactionYearMonth', 'transactionYearMonthWeek', 'transactionYearMonthDay'].includes(value)
        ? _.find(_groupingCriteria, ['value', value]).name
        : _.find(_groupingCriteria, ['value', 'transactionYearMonth']).name
    )

    let bar_pie_transaction_list
    if (
      ['transactionYearQuarter', 'senderName', 'status', 'currency'].includes(value)
    ) {
      bar_pie_transaction_list = currentTransactionList(_transactionList, value)
      setInstanceState('pieData', pieDataOption, bar_pie_transaction_list.map((item) => item.subTotal))
      setInstanceState('barData', barDataOption, getInstanceState('pieData', pieDataOption, []))
      setInstanceState(
        'pieLabel',
        pieLabelOption,
        bar_pie_transaction_list.map((item) =>
          value === 'status' ? transactionStatus(item.groupBy)?.status : item.groupBy
        )
      )
      setInstanceState('barLabel', barLabelOption, getInstanceState('pieLabel', pieLabelOption, []))
    }
    setInstanceState('bar_name', bar_nameOption, _.find(_groupingCriteria, ['value', value]).name)

    const chart_data = [
      {
        type: 'bar',
        data: getInstanceState('barData', barDataOption, []),
        categories: getInstanceState('barLabel', barLabelOption, []),
        name: getInstanceState('bar_name', bar_nameOption, ''),
      },
      {
        type: 'area',
        data: getInstanceState('dataValue', dataValueOption, []),
        categories: getInstanceState('axisData', axisDataOption, []),
        name: getInstanceState('area_name', area_nameOption, ''),
      },
      {
        type: 'pie',
        series: getInstanceState('pieData', pieDataOption, []),
        labels: getInstanceState('pieLabel', pieLabelOption, []),
        name: getInstanceState('bar_name', bar_nameOption, ''),
      },
    ]
    apimoneyStore.setTransactionList(chart_data)
    if (!init_status && chart_data.length > 0) {
      doUpdateChart()
      doUpdateTheme()
    }
    f7.preloader.hide()
  }

  function doUpdateChart() {
    const _structuredTransactionList = structuredTransactionList.value
    setInstanceState(
      'areaChartSeries',
      areaChartSeriesOption,
      [
        {
          name: _structuredTransactionList
            ? _structuredTransactionList.find((item) => item.type === 'area').name
            : [],
          data: _structuredTransactionList
            ? _structuredTransactionList.find((item) => item.type === 'area').data
            : [],
        },
      ]
    )
    setInstanceState(
      'barChartSeries',
      barChartSeriesOption,
      [
        {
          data: _structuredTransactionList
            ? _structuredTransactionList.find((item) => item.type === 'bar').data
            : [],
        },
      ]
    )
    setInstanceState(
      'pieChartSeries',
      pieChartSeriesOption,
      _structuredTransactionList
        ? _structuredTransactionList.find((item) => item.type === 'pie').series
        : []
    )
  }

  function doUpdateTheme() {
    const _structuredTransactionList = structuredTransactionList.value
    setInstanceState(
      'areaChartOptions',
      areaChartOptionsOption,
      {
        xaxis: {
          categories: _structuredTransactionList
            ? _structuredTransactionList.find((item) => item.type === 'area').categories
            : [],
        },
      }
    )
    setInstanceState(
      'barChartOptions',
      barChartOptionsOption,
      {
        xaxis: {
          categories: _structuredTransactionList
            ? _structuredTransactionList.find((item) => item.type === 'bar').categories
            : [],
        },
      }
    )
    setInstanceState(
      'pieChartOptions',
      pieChartOptionsOption,
      {
        labels: _structuredTransactionList
          ? _structuredTransactionList.find((item) => item.type === 'pie').labels
          : [],
      }
    )
  }

  return {
    bankAccount,
    bankAccountList,
    standard,
    business,
    accountList,
    transactionList,
    apiWallet,
    structuredTransactionList,
    profile,
    isDeskTop,
    isTablet,
    documentsUrl,
    document,
    locale,
    getProfile,
    isAuthenticated,
    isProfileLoaded,
    getusererror,
    userAccount,
    transactionStatus,
    selectSortingCriteria,
    getCurrentUser,
    currentTransactionList,
    setTransactionList: doSetTransactionList,
    updateChart: doUpdateChart,
    updateTheme: doUpdateTheme,
  }
}
